<template>
  <div class="permission-management app-page app-page--fill">
    <el-card class="main-card">
      <div class="main-content" ref="mainContentRef">
        <!-- 左侧部门树 -->
        <div class="left-panel" ref="leftPanelRef" :style="{ width: leftPanelWidth + 'px' }">
          <div class="panel-header">
            <span class="panel-title">部门结构</span>
            <el-button
              type="primary"
              :icon="Refresh"
              circle
              size="small"
              class="app-refresh-btn"
              @click="loadDepartmentTree"
              :loading="treeLoading"
              title="刷新"
            />
          </div>
          <div class="search-box">
            <el-input
              v-model="filterText"
              placeholder="搜索部门"
              :prefix-icon="Search"
              clearable
              size="default"
              @keyup.enter="handleDepartmentSearch"
              @clear="handleDepartmentSearch"
            />
            <el-button type="primary" size="default" @click="handleDepartmentSearch">搜索</el-button>
          </div>
          <div class="tree-wrapper" v-loading="treeLoading">
            <el-tree
              ref="treeRef"
              :data="departmentTree"
              :props="treeProps"
              node-key="value"
              :filter-node-method="filterNode"
              :expand-on-click-node="false"
              highlight-current
              @node-click="handleNodeClick"
            >
              <template #default="{ node }">
                <span class="tree-node">
                  <el-icon class="node-icon"><OfficeBuilding /></el-icon>
                  <span class="node-label">{{ node.label }}</span>
                </span>
              </template>
            </el-tree>
            <el-empty v-if="!treeLoading && departmentTree.length === 0" description="暂无部门数据" />
          </div>
          <div class="tree-options">
            <el-checkbox v-model="showSubUsers" @change="handleShowSubUsersChange">显示下级人员</el-checkbox>
          </div>
        </div>

        <!-- 拖拽分隔条 -->
        <div class="resize-handle" @mousedown="startResize"></div>

        <!-- 右侧人员列表 -->
        <div class="right-panel" v-loading="userLoading">
          <div class="panel-header">
            <span class="panel-title">
              {{ currentDepartment ? currentDepartment.label : '请选择部门' }}
              <el-tag v-if="currentDepartment" type="info" size="small" style="margin-left: 8px;">
                {{ pagination.total }} 人
              </el-tag>
            </span>
            <div class="header-actions" v-if="currentDepartment">
              <el-button
                type="primary"
                :icon="Refresh"
                circle
                size="small"
                class="app-refresh-btn"
                @click="loadUserList"
                title="刷新"
              />
            </div>
          </div>
          
          <div class="user-content" v-if="currentDepartment">
            <!-- 搜索和筛选 -->
            <div class="filter-bar">
              <el-input
                v-model="userSearchText"
                placeholder="搜索人员姓名"
                :prefix-icon="Search"
                clearable
                style="width: 240px;"
                @clear="handleUserSearch"
                @keyup.enter="handleUserSearch"
              />
              <el-button type="primary" size="default" @click="handleUserSearch">搜索</el-button>
            </div>
            
            <!-- 人员表格 -->
            <div class="table-wrapper" ref="tableWrapperRef">
              <el-table
                :data="displayUserList"
                stripe
                border
                style="width: 100%;"
                :max-height="tableMaxHeight"
              >
                <el-table-column type="index" label="序号" width="60" align="center" :index="getTableIndex" />
                <el-table-column prop="uRealName" label="姓名" min-width="100">
                  <template #default="{ row }">
                    <div class="user-name-cell">
                      <el-avatar :size="32">
                        {{ (row.uRealName || '').charAt(0) }}
                      </el-avatar>
                      <span>{{ row.uRealName || '-' }}</span>
                    </div>
                  </template>
                </el-table-column>
                <el-table-column prop="uLoginName" label="账号" min-width="130" />
                <el-table-column prop="DepartmentName" label="部门" min-width="130" />
                <el-table-column prop="uRemark" label="创建人" min-width="120" show-overflow-tooltip />
                <el-table-column prop="RoleNames" label="角色" min-width="140">
                  <template #default="{ row }">
                    <template v-if="row.RoleNames && row.RoleNames.length > 0">
                      <el-tag v-for="(role, idx) in row.RoleNames" :key="idx" type="primary" size="small" style="margin: 2px;">
                        {{ role }}
                      </el-tag>
                    </template>
                    <span v-else>-</span>
                  </template>
                </el-table-column>
              
                <el-table-column prop="DataPermissions" label="数据权限" min-width="120">
                  <template #default="{ row }">
                    <el-tag v-if="row.DataPermissions" type="warning" size="small">{{ row.DataPermissions }}</el-tag>
                    <span v-else>-</span>
                  </template>
                </el-table-column>

                <el-table-column prop="uUpdateTime" label="更新时间" min-width="140" show-overflow-tooltip />
                
                <el-table-column v-if="can('保存分配')" label="操作" width="100" align="center" fixed="right">
                  <template #default="{ row }">
                    <el-button type="primary" :icon="Key" size="small" @click="handlePermission(row)">
                      权限
                    </el-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
            
            <!-- 分页 -->
            <div class="pagination-wrapper" v-if="pagination.total > 0">
              <el-pagination
                v-model:current-page="pagination.page"
                v-model:page-size="pagination.pageSize"
                :page-sizes="[10, 20, 50, 100]"
                :total="pagination.total"
                layout="total, sizes, prev, pager, next, jumper"
                small
                @size-change="handleSizeChange"
                @current-change="handlePageChange"
              />
            </div>
          </div>
          
          <div class="empty-state" v-else>
            <el-empty description="请在左侧选择一个部门查看人员信息" />
          </div>
        </div>
      </div>
    </el-card>
    
    <!-- 权限配置弹窗 -->
    <el-dialog
      v-model="permissionDialogVisible"
      :title="`角色列表 - ${currentUser?.uRealName || ''}`"
      width="600px"
      :close-on-click-modal="false"
      :teleported="true"
      :lock-scroll="true"
      class="permission-role-dialog"
    >
      <div class="permission-dialog-content" v-loading="roleLoading">
        <el-descriptions :column="1" border style="margin-bottom: 16px;">
          <el-descriptions-item label="姓名">{{ currentUser?.uRealName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="账号">{{ currentUser?.uLoginName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="部门">{{ currentUser?.DepartmentName || currentDepartment?.label || '-' }}</el-descriptions-item>
          <el-descriptions-item label="数据权限">
            <el-select v-model="selectedDataPermission" placeholder="请选择数据权限" style="width: 200px;">
              <el-option label="个人数据" value="个人数据" />
              <el-option label="部门数据" value="部门数据" />
              <el-option label="全部数据" value="全部数据" />
            </el-select>
          </el-descriptions-item>
        </el-descriptions>

        <div class="role-list-wrapper" v-if="!roleLoading && roleList.length === 0">
          <el-empty description="暂无角色数据" />
        </div>

        <el-scrollbar v-else class="role-list-scroll">
          <el-checkbox-group v-model="selectedRoleIds" class="role-list">
            <el-checkbox
              v-for="role in roleList"
              :key="role.Id"
              :label="role.Id"
              :disabled="!role.Enabled || role.IsDeleted"
              class="role-checkbox"
            >
              <div class="role-item">
                <div class="role-item__name">{{ role.Name }}</div>
                <div class="role-item__desc" v-if="role.Description">{{ role.Description }}</div>
              </div>
            </el-checkbox>
          </el-checkbox-group>
        </el-scrollbar>
      </div>
      <template #footer>
        <el-button @click="permissionDialogVisible = false">取消</el-button>
        <el-button v-if="can('保存分配')" type="primary" :loading="roleSubmitting" @click="handleConfirmRole">角色分配</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, reactive, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Refresh, Key, OfficeBuilding } from '@element-plus/icons-vue'
import type { ElTree } from 'element-plus'
import { PermissionManagement } from '@/network'
import { useButtonPermission } from '@/utils/buttonPermission'

const { can } = useButtonPermission('/QuanXianGuanLi/fenpei.vue')

// 类型定义
interface DepartmentNode {
  value: string
  Pid: string
  label: string
  order: number
  disabled: boolean
  children: DepartmentNode[] | null
}

interface RoleInfo {
  Id: string
  Name: string
  Description: string | null
  Enabled: boolean
  OrderSort: number
  IsDeleted: boolean
}

interface UserInfo {
  uID: string
  uLoginName: string
  uRealName: string
  uStatus: number
  DepartmentId: string
  DepartmentName: string
  uRemark: string
  uCreateTime: string
  uUpdateTime: string
  uErrorCount: number
  name: string | null
  sex: number
  age: number
  addr: string | null
  tdIsDelete: boolean
  RoleNames: string[]
  DataPermissions: string
  Dids: string[]
  RIDs: string[]
}

// 树形控件相关
const treeRef = ref<InstanceType<typeof ElTree>>()
const treeProps = {
  children: 'children',
  label: 'label',
  disabled: 'disabled'
}

// 布局相关
const mainContentRef = ref<HTMLElement | null>(null)
const leftPanelRef = ref<HTMLElement | null>(null)
const tableWrapperRef = ref<HTMLElement | null>(null)
const leftPanelWidth = ref<number>(400)
const isResizing = ref<boolean>(false)
const minPanelWidth = 200
const tableMaxHeight = ref<number>(500)
let tableResizeObserver: ResizeObserver | null = null

// 数据相关
const departmentTree = ref<DepartmentNode[]>([])
const currentDepartment = ref<DepartmentNode | null>(null)
const userList = ref<UserInfo[]>([])
const filterText = ref('')
const userSearchText = ref('')
const showSubUsers = ref(false)

// 加载状态
const treeLoading = ref(false)
const userLoading = ref(false)

// 分页
const pagination = reactive({
  page: 1,
  pageSize: 20,
  total: 0
})

const displayUserList = computed(() => {
  const start = (pagination.page - 1) * pagination.pageSize
  const end = start + pagination.pageSize
  return userList.value.slice(start, end)
})

// 权限弹窗
const permissionDialogVisible = ref(false)
const currentUser = ref<UserInfo | null>(null)
const roleList = ref<RoleInfo[]>([])
const selectedRoleIds = ref<string[]>([])
const roleLoading = ref(false)
const roleSubmitting = ref(false)

// 数据权限
const selectedDataPermission = ref('')

// 部门搜索处理
const handleDepartmentSearch = () => {
  treeRef.value?.filter(filterText.value)
}

// 树节点筛选方法
const filterNode = (value: string, data: DepartmentNode) => {
  if (!value) return true
  return data.label.includes(value)
}

// 拖拽调整宽度
const startResize = () => {
  isResizing.value = true
  document.addEventListener('mousemove', handleResizePanel)
  document.addEventListener('mouseup', stopResize)
  document.body.style.cursor = 'col-resize'
  document.body.style.userSelect = 'none'
}

const handleResizePanel = (e: MouseEvent) => {
  if (!isResizing.value || !mainContentRef.value) return
  const containerRect = mainContentRef.value.getBoundingClientRect()
  const maxWidth = containerRect.width * 0.5
  let newWidth = e.clientX - containerRect.left
  newWidth = Math.max(minPanelWidth, Math.min(maxWidth, newWidth))
  leftPanelWidth.value = newWidth
}

const stopResize = () => {
  isResizing.value = false
  document.removeEventListener('mousemove', handleResizePanel)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.cursor = ''
  document.body.style.userSelect = ''
}

const stopTableResizeObserver = () => {
  tableResizeObserver?.disconnect()
  tableResizeObserver = null
}

const syncTableResizeObserver = () => {
  stopTableResizeObserver()

  if (!tableWrapperRef.value || typeof ResizeObserver === 'undefined') return

  tableResizeObserver = new ResizeObserver(() => {
    calculateTableHeight()
  })
  tableResizeObserver.observe(tableWrapperRef.value)
}

// 计算表格最大高度
const calculateTableHeight = () => {
  nextTick(() => {
    const tableWrapperEl = tableWrapperRef.value
    if (!tableWrapperEl) return

    const computedStyle = window.getComputedStyle(tableWrapperEl)
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
    const availableHeight = tableWrapperEl.clientHeight - paddingTop - paddingBottom - 8

    tableMaxHeight.value = Math.max(Math.floor(availableHeight), 240)
  })
}

// 加载部门树
const loadDepartmentTree = async () => {
  try {
    treeLoading.value = true
    const res: any = await PermissionManagement.getDepartmentTree({ pid: '0' })

    if (res && res.status === 200 && res.success && res.response) {
      if (res.response.children && Array.isArray(res.response.children)) {
        departmentTree.value = res.response.children
      } else {
        departmentTree.value = [res.response]
      }
    } else {
      departmentTree.value = []
      ElMessage.warning('获取部门数据失败')
    }
  } catch (error) {
    console.error('加载部门树失败:', error)
    departmentTree.value = []
    ElMessage.error('加载部门数据失败')
  } finally {
    treeLoading.value = false
  }
}

// 点击树节点
const handleNodeClick = (data: DepartmentNode) => {
  currentDepartment.value = data
  userSearchText.value = ''
  pagination.page = 1
  loadUserList()
}

// 显示下级人员复选框切换
const handleShowSubUsersChange = () => {
  if (currentDepartment.value) {
    pagination.page = 1
    loadUserList()
  }
}

// 人员姓名搜索
const handleUserSearch = () => {
  pagination.page = 1
  loadUserList()
}

// 加载用户列表
const loadUserList = async () => {
  if (!currentDepartment.value) return
  
  try {
    userLoading.value = true
    const res: any = await PermissionManagement.getDepartmentUser({
      nameKey: userSearchText.value || undefined,
      departmentId: currentDepartment.value.value,
      includesSubordinates: showSubUsers.value,
    })

    if (res && res.status === 200 && res.success && res.response) {
      userList.value = Array.isArray(res.response.data) ? res.response.data : []
      pagination.total = userList.value.length

      const maxPage = Math.max(1, Math.ceil(pagination.total / pagination.pageSize))
      if (pagination.page > maxPage) {
        pagination.page = maxPage
      }
    } else {
      userList.value = []
      pagination.total = 0
      ElMessage.warning('获取人员数据失败')
    }
  } catch (error) {
    console.error('加载用户列表失败:', error)
    userList.value = []
    pagination.total = 0
    ElMessage.error('加载人员数据失败')
  } finally {
    userLoading.value = false
  }
}

// 分页处理
const handleSizeChange = (size: number) => {
  pagination.pageSize = size
  pagination.page = 1
  calculateTableHeight()
}

const handlePageChange = (page: number) => {
  pagination.page = page
  calculateTableHeight()
}

const getTableIndex = (index: number) => {
  return (pagination.page - 1) * pagination.pageSize + index + 1
}

// 加载角色列表
const loadRoleList = async () => {
  try {
    roleLoading.value = true
    const res: any = await PermissionManagement.getAllRole()

    if (res && res.status === 200 && res.success && res.response) {
      roleList.value = Array.isArray(res.response.data) ? res.response.data : []
    } else {
      roleList.value = []
      ElMessage.warning('获取角色列表失败')
    }
  } catch (error) {
    console.error('加载角色列表失败:', error)
    roleList.value = []
    ElMessage.error('加载角色列表失败')
  } finally {
    roleLoading.value = false
  }
}

// 权限配置
const handlePermission = (user: UserInfo) => {
  currentUser.value = { ...user }
  selectedRoleIds.value = user.RIDs ? [...user.RIDs] : []
  selectedDataPermission.value = user.DataPermissions || ''
  permissionDialogVisible.value = true
  loadRoleList()
}

// 确认角色分配
const handleConfirmRole = async () => {
  if (!currentUser.value) return
  try {
    roleSubmitting.value = true

    // 1. 保存角色权限
    const payload = {
      ...currentUser.value,
      RIDs: selectedRoleIds.value
    }
    const res: any = await PermissionManagement.updateUseRole(payload)

    if (res && res.status === 200 && res.success) {
      ElMessage.success('角色分配成功')
    } else {
      ElMessage.warning(res?.msg || '角色分配失败')
      return
    }

    // 2. 保存数据权限
    if (selectedDataPermission.value && currentUser.value.uID) {
      try {
        const dataPermRes: any = await PermissionManagement.SetUserDataPermissions({
          userId: currentUser.value.uID,
          Permissions: selectedDataPermission.value
        })
        if (dataPermRes && dataPermRes.status === 200 && dataPermRes.success) {
          ElMessage.success('数据权限设置成功')
        }
      } catch (dataErr) {
        console.error('数据权限保存失败:', dataErr)
        ElMessage.warning('数据权限保存失败，请重试')
      }
    }

    permissionDialogVisible.value = false
    loadUserList()
  } catch (error) {
    console.error('角色分配失败:', error)
    ElMessage.error('角色分配失败')
  } finally {
    roleSubmitting.value = false
  }
}

// 组件挂载
watch(tableWrapperRef, () => {
  syncTableResizeObserver()
  calculateTableHeight()
})

onMounted(() => {
  loadDepartmentTree()
  calculateTableHeight()
  window.addEventListener('resize', calculateTableHeight)
  
  nextTick(() => {
    if (mainContentRef.value) {
      const containerWidth = mainContentRef.value.getBoundingClientRect().width
      leftPanelWidth.value = Math.min(400, containerWidth * 0.3)
    }

    syncTableResizeObserver()
  })
})

onUnmounted(() => {
  document.removeEventListener('mousemove', handleResizePanel)
  document.removeEventListener('mouseup', stopResize)
  window.removeEventListener('resize', calculateTableHeight)
  stopTableResizeObserver()
})
</script>

<style scoped lang="less">
.permission-management {
  /* 页面 padding / 背景 / 滚动由 layout 统一处理 */
  min-height: 0;

  .main-card {
    flex: 1;
    height: 0;
    max-height: 100%;
    display: flex;
    flex-direction: column;

    :deep(.el-card__body) {
      flex: 1;
      height: 0;
      padding: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }

  .main-content {
    display: flex;
    flex: 1;
    height: 0;
    min-height: 0;
    overflow: hidden;

    .resize-handle {
      width: 6px;
      background: #e4e7ed;
      cursor: col-resize;
      flex-shrink: 0;
      transition: background-color 0.2s;
      position: relative;

      &:hover {
        background: #409eff;
      }

      &::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 2px;
        height: 30px;
        background: #909399;
        border-radius: 1px;
      }

      &:hover::before {
        background: white;
      }
    }

    // 左侧部门树面板
    .left-panel {
      flex-shrink: 0;
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
      background: #fff;

      .panel-header {
        padding: 12px 16px;
        border-bottom: 1px solid #e4e7ed;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .panel-title {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
        }
      }

      .search-box {
        padding: 12px 16px;
        border-bottom: 1px solid #f0f0f0;
        display: flex;
        gap: 8px;
        align-items: center;
        
        .el-input {
          flex: 1;
        }
      }

      .tree-options {
        padding: 8px 16px;
        border-top: 1px solid #f0f0f0;
        flex-shrink: 0;
      }

      .tree-wrapper {
        flex: 1;
        overflow-y: auto;
        padding: 8px 12px;
        overscroll-behavior: contain;
        scrollbar-gutter: stable;
        scrollbar-width: thin;
        scrollbar-color: #c0c4cc #f5f7fa;

        &::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        &::-webkit-scrollbar-track {
          background: #f5f7fa;
          border-radius: 8px;
        }

        &::-webkit-scrollbar-thumb {
          background: #c0c4cc;
          border-radius: 8px;
          border: 2px solid #f5f7fa;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: #a8abb2;
        }

        .tree-node {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 4px 0;

          .node-icon {
            color: #409eff;
            font-size: 16px;
          }

          .node-label {
            font-size: 14px;
          }
        }

        :deep(.el-tree-node__content) {
          height: 36px;
          border-radius: 4px;
          transition: background-color 0.2s;

          &:hover {
            background-color: #ecf5ff;
          }
        }

        :deep(.el-tree-node.is-current > .el-tree-node__content) {
          background-color: #ecf5ff;
          color: #409eff;

          .node-icon {
            color: #409eff;
          }
        }
      }
    }

    // 右侧人员列表面板
    .right-panel {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
      overflow: hidden;
      background: #fff;

      .panel-header {
        padding: 12px 16px;
        border-bottom: 1px solid #e4e7ed;
        display: flex;
        align-items: center;
        justify-content: space-between;

        .panel-title {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
          display: flex;
          align-items: center;
        }

        .header-actions {
          display: flex;
          gap: 8px;
        }
      }

      .user-content {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        overflow: hidden;

        .filter-bar {
          padding: 12px 16px;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          gap: 12px;
          align-items: center;
        }

        .table-wrapper {
          flex: 1;
          min-height: 0;
          padding: 12px 16px;
          overflow: hidden;

          .user-name-cell {
            display: flex;
            align-items: center;
            gap: 8px;

            .el-avatar {
              background-color: #409eff;
              color: #fff;
              font-size: 14px;
            }
          }
        }

        .pagination-wrapper {
          padding: 12px 16px;
          border-top: 1px solid #f0f0f0;
          display: flex;
          justify-content: flex-end;
          flex-shrink: 0;
        }
      }

      .empty-state {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
  }
}

// 权限弹窗内容
.permission-dialog-content {
  padding: 16px 0;
}

:deep(.permission-role-dialog .el-dialog__body) {
  overflow: hidden;
  
}

:deep(.permission-role-dialog) {
  max-height: 75vh;

}


.role-list-wrapper {
  min-height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.role-list-scroll {
  max-height: 70vh;
  height: 260px;
}

.role-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 16px;
  padding-right: 10px;
  align-items: start;
  grid-auto-rows: max-content;
}

.role-checkbox {
  display: flex;
  align-items: flex-start;
  height: auto;
  min-height: 32px;
  margin-right: 0;
  white-space: normal;
}

:deep(.role-checkbox .el-checkbox__label) {
  display: block;
  min-width: 0;
  padding-left: 8px;
  white-space: normal;
}

.role-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.role-item__name {
  font-size: 14px;
  line-height: 20px;
  color: #303133;
}

.role-item__desc {
  font-size: 12px;
  line-height: 16px;
  color: #909399;
  margin-top: 2px;
  overflow-wrap: anywhere;
}

// 响应式设计
@media (max-width: 768px) {
  .permission-management {
    .main-content {
      flex-direction: column;

      .resize-handle {
        display: none;
      }

      .left-panel {
        width: 100% !important;
        max-height: 200px;
        border-bottom: 1px solid #e4e7ed;
      }

      .right-panel {
        flex: 1;
      }
    }
  }

  .role-list {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}
</style>
